/**
 * Work Order Operation Search Utilities
 *
 * Generic utilities for parsing and handling multi-field work order operation searches.
 * These utilities are shared across barcode scanning and manual search functionality.
 */

// Work order operation field names for multi-field search
export const WORK_ORDER_OPERATION_FIELDS = {
  WORK_ORDER_ID: 'workOrderId',
  SEQUENCE_NUMBER: 'sequenceNumber',
  LOT: 'lot',
  SUB: 'sub',
  SPLIT: 'split',
};

const FIELD_DELIMITER = ' | ';
const FIELD_SEPARATOR = ': ';

/**
 * Parse a search string into individual work order operation fields
 *
 * @param {string} searchString - Search string with format "workOrderId: 123 | sequenceNumber: 10"
 * @returns {Object} Object with field names as keys and values
 *
 * @example
 * parseSearchFields("workOrderId: 123 | sequenceNumber: 10")
 * // Returns: { workOrderId: "123", sequenceNumber: "10" }
 */
export const parseSearchFields = (searchString) => {
  if (!searchString || typeof searchString !== 'string') {
    return {};
  }

  const fields = {};
  const parts = searchString.split(FIELD_DELIMITER).filter((part) => {
    return part.trim();
  });

  parts.forEach((part) => {
    const trimmedPart = part.trim();
    const separatorIndex = trimmedPart.indexOf(FIELD_SEPARATOR);

    if (separatorIndex > -1) {
      const fieldName = trimmedPart.substring(0, separatorIndex).trim();
      const value = trimmedPart
        .substring(separatorIndex + FIELD_SEPARATOR.length)
        .trim();

      if (
        fieldName &&
        value &&
        Object.values(WORK_ORDER_OPERATION_FIELDS).includes(fieldName)
      ) {
        fields[fieldName] = value;
      }
    } else {
      // Fallback: If no separator, assume it's workOrderId for backwards compatibility
      fields[WORK_ORDER_OPERATION_FIELDS.WORK_ORDER_ID] = trimmedPart;
    }
  });

  return fields;
};

/**
 * Check if search string contains barcode field prefixes
 * Barcode format: "fieldName: value | fieldName: value"
 *
 * @param {string} searchString - Search string to check
 * @returns {boolean} True if contains field prefixes (barcode format)
 *
 * @example
 * isBarcodeFormattedSearch("workOrderId: 123")
 * // Returns: true
 *
 * @example
 * isBarcodeFormattedSearch("12345")
 * // Returns: false
 */
export const isBarcodeFormattedSearch = (searchString) => {
  if (!searchString || typeof searchString !== 'string') {
    return false;
  }
  return searchString.includes(FIELD_SEPARATOR);
};
